// Digitación de Compras como la de Ventas (pedido de Rodrigo, 2026-10-20: «en Random el proceso de
// compra es visualmente parecido al de venta»). UNA ventana para los tres documentos:
//   OCC  orden de compra           → lo que se pide al proveedor (no mueve stock).
//   GRC  guía de recepción         → lo que llegó: ENTRA AL STOCK al grabar (lo roto no entra).
//   FCC  factura de compra         → la deuda. Llegó con la factura (entra al stock ahora), ya entró
//                                    con guías (se cruza con ellas, sin digitar) o llegará después.
//   ISF  ingreso sin factura       → mercadería que compró un tercero (Costa Delivery a Andina) y que
//                                    no se le facturará a MADECI: entra al stock con sus costos y la
//                                    deuda con quien se le paga, sin ir al registro de compras del SII.
// Cabecera de dos filas (proveedor, número, emisión, vence, bodega), grilla teclado-primero (código →
// Enter → cantidad → precio → …), y pie con los totales. El flete puede venir EN LA LÍNEA del producto
// (es de ese producto) o en UN TOTAL: se reparte como diga el proveedor (por valor, por unidades o por
// su costo logístico por producto, p. ej. CCU). La columna «Costo unit.» muestra el costo final de
// cada producto con su flete.
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { api } from '../api/cliente';
import type { Presentacion } from '../api/contratos';
import { useProducto } from '../api/consultas';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { decimal, fecha, pesos } from '../comun/formato';
import { NuevoProducto } from '../configuracion/NuevoProducto';
import { useSesion } from '../sesion/SesionContexto';
import { useBodegas } from '../stock/PantallasStock';
import { BuscadorCliente } from '../ventas/BuscadorCliente';
import { BuscadorProducto, type ProductoElegido } from '../ventas/BuscadorProducto';
import { CantidadRtu } from '../ventas/CantidadRtu';
import { leerCantidad } from '../ventas/cantidad';
import { resolverCodigo } from '../ventas/codigo';
import { estimar, hoy } from './comun';
import {
  useAccionCompras,
  type CompraDeProducto,
  type FacturaLeida,
  type OrdenLeida,
} from './datos';

export type TipoDocCompra = 'OCC' | 'GRC' | 'FCC' | 'ISF';

export const NOMBRE_DOC_COMPRA: Record<TipoDocCompra, string> = {
  OCC: 'Orden de compra',
  GRC: 'Guía de recepción',
  FCC: 'Factura de compra',
  ISF: 'Ingreso sin factura',
};

/** Tipo del documento de origen de un ingreso sin factura (va en su referencia). */
const NOMBRE_TIPO_ORIGEN: Record<number, string> = {
  33: 'Factura',
  34: 'Factura exenta',
  46: 'Factura de compra',
};

type Llegada = 'AHORA' | 'GUIAS' | 'DESPUES';
type Reparto = 'PROVEEDOR' | 'FLETE_POR_VALOR' | 'FLETE_POR_UNIDADES' | 'FLETE_POR_COSTO_LOGISTICO';

const NOMBRE_REPARTO: Record<Exclude<Reparto, 'PROVEEDOR'>, string> = {
  FLETE_POR_VALOR: 'por valor',
  FLETE_POR_UNIDADES: 'por unidades',
  FLETE_POR_COSTO_LOGISTICO: 'por costo logístico',
};

interface Proveedor {
  id: string;
  rut: string | null;
  dv: string | null;
  razonSocial: string;
  plazoDias: number | null;
}

export interface LineaCompra {
  clave: string;
  productoId: string;
  presentacionId: string;
  sku: string;
  nombre: string;
  unidad: string;
  factor: number;
  cantidad: string;
  /** Solo guía: lo que llegó roto o no se acepta (no entra al stock). */
  rechazada: string;
  motivo: string;
  precio: string;
  /** Descuento en % (como en Random). */
  desc: string;
  /** Solo factura: flete que el proveedor cobró EN ESTA LÍNEA. */
  flete: string;
  /** De una guía ya recibida (factura que cruza) o de una línea de la OC (guía contra OC). */
  recepcionLineaId?: string;
  ordenLineaNumero?: number;
}

interface GuiaPorFacturar {
  recepcionId: string;
  numeroVisible: string;
  fecha: string;
  guia: string | null;
  ordenId: string | null;
  lineas: Array<{
    recepcionLineaId: string;
    numero: number;
    productoId: string;
    presentacionId: string;
    presentacionCodigo: string;
    sku: string;
    descripcion: string;
    factorABase: string;
    pendiente: string;
    precioOc: string | null;
    ordenLineaNumero: number | null;
  }>;
}

interface CostoLogistico {
  productoId: string;
  montoBase: string | null;
}

/** Los impuestos que el maestro espera para un producto a la fecha de emisión. */
interface ImpuestosDeProducto {
  productoId: string;
  impuestos: Array<{ codigo: string; tipo: string; tasa: string }>;
  error?: string;
}

/** Un impuesto adicional (ILA…) de la factura: calculado sobre las líneas que lo llevan. */
interface AdicionalCalculado {
  codigo: string;
  tasa: string;
  /** Neto de las líneas cuyos productos lo llevan (el flete no paga ILA). */
  base: number;
  calculado: number;
  /** Lo que se graba: el calculado o lo que se ajustó a la factura. */
  monto: number;
  /** Índices de las líneas que lo soportan y lo que le toca a cada una (suma = monto). */
  lineas: number[];
  porLinea: number[];
}

const num = (t: string) => estimar(t);
const redondo = (x: number) => Math.round(x);

/**
 * Reparte un monto entero en proporción a los pesos, al peso exacto: la suma de las partes es el
 * monto (lo que sobra por redondeo va a los mayores restos).
 */
export function prorratear(monto: number, pesos: readonly number[]): number[] {
  if (!pesos.length) return [];
  const total = pesos.reduce((a, b) => a + Math.max(0, b), 0);
  if (total <= 0) return pesos.map((_, i) => (i === 0 ? monto : 0));
  const exactos = pesos.map((p) => (monto * Math.max(0, p)) / total);
  const partes = exactos.map((x) => Math.floor(x));
  let resto = monto - partes.reduce((a, b) => a + b, 0);
  const orden = exactos
    .map((x, i) => ({ i, r: x - Math.floor(x) }))
    .sort((a, b) => b.r - a.r || a.i - b.i);
  for (let k = 0; resto > 0; k = (k + 1) % orden.length, resto--) partes[orden[k]!.i]! += 1;
  return partes;
}

/** «0.205» → «20,5 %». */
const porcentaje = (tasa: string) =>
  `${(Math.round(Number(tasa) * 100000) / 1000).toLocaleString('es-CL')} %`;
let secuencia = 0;
const nuevaClave = () => `l${Date.now().toString(36)}${(secuencia++).toString(36)}`;

/** Neto de la línea: cantidad × precio − descuento (% o monto). */
export function netoLinea(l: Pick<LineaCompra, 'cantidad' | 'precio' | 'desc'>): number {
  const bruto = num(l.cantidad) * num(l.precio);
  return redondo(bruto - descuentoLinea(l));
}
/**
 * El descuento se digita en % (como en Random) o, con «$» delante, como el MONTO que trae la factura
 * (2026-10-29: Andina imprime el % redondeado a 2 decimales y su monto no siempre calza con él).
 */
export function descuentoLinea(l: Pick<LineaCompra, 'cantidad' | 'precio' | 'desc'>): number {
  const t = l.desc.trim();
  if (t.startsWith('$')) return Math.max(0, redondo(num(t.slice(1).replace(/\./g, ''))));
  const pct = num(t);
  return pct > 0 ? redondo((num(l.cantidad) * num(l.precio) * pct) / 100) : 0;
}

/**
 * El flete que le toca a cada línea (estimado, como lo repartirá el motor): el de la línea, más su
 * parte del flete global según la estrategia. Suma exacta al peso (el último absorbe el redondeo).
 */
export function repartirFleteLineas(
  lineas: readonly LineaCompra[],
  global: number,
  estrategia: Exclude<Reparto, 'PROVEEDOR'> | null,
  costos: ReadonlyMap<string, number>,
): number[] {
  const propio = lineas.map((l) => redondo(num(l.flete)));
  if (!global || !estrategia || !lineas.length) return propio;
  const pesosReparto = lineas.map((l) => {
    const base = num(l.cantidad) * l.factor;
    if (estrategia === 'FLETE_POR_VALOR') return netoLinea(l);
    if (estrategia === 'FLETE_POR_UNIDADES') return base;
    return base * (costos.get(l.productoId) ?? 0);
  });
  const total = pesosReparto.reduce((a, b) => a + b, 0);
  if (!total) return propio;
  let resto = global;
  return propio.map((p, i) => {
    const parte = i === lineas.length - 1 ? resto : redondo((global * pesosReparto[i]!) / total);
    resto -= parte;
    return p + parte;
  });
}

function usePresentaciones(empresaId: string, productoId: string) {
  const q = useProducto(empresaId, productoId);
  return (q.data?.presentaciones ?? []) as Presentacion[];
}

/**
 * La unidad de la línea: se puede cambiar (caja ↔ unidad) y el precio se convierte. Con `conservarBase`
 * (línea de una guía ya recibida) solo se ofrecen las unidades en que lo recibido cabe exacto.
 */
function SelectorUnidad({
  empresaId,
  linea,
  editable,
  conservarBase = false,
  alCambiar,
}: {
  empresaId: string;
  linea: LineaCompra;
  editable: boolean;
  conservarBase?: boolean;
  alCambiar: (p: Presentacion) => void;
}) {
  const base = num(linea.cantidad) * linea.factor;
  const cabe = (p: Presentacion) => {
    const f = Number(p.factor_a_base) || 1;
    return Math.abs(Number(limpio(base / f)) * f - base) < 1e-6;
  };
  const pres = usePresentaciones(empresaId, linea.productoId).filter(
    (p) => p.activa && (!conservarBase || !base || p.id === linea.presentacionId || cabe(p)),
  );
  if (!editable || pres.length < 2) return <span className="mono">{linea.unidad}</span>;
  return (
    <select
      className="celda-sel"
      aria-label={`Unidad ${linea.sku}`}
      value={linea.presentacionId}
      tabIndex={-1}
      onChange={(e) => {
        const p = pres.find((x) => x.id === e.target.value);
        if (p) alCambiar(p);
      }}
    >
      {pres.map((p) => (
        <option key={p.id} value={p.id}>
          {p.codigo}
          {Number(p.factor_a_base) !== 1 ? ` ×${decimal(p.factor_a_base)}` : ''}
        </option>
      ))}
    </select>
  );
}

/** Cajas y displays con que se compra el producto (sin exigir que se vendan), de menor a mayor. */
function cajasDeCompra(pres: readonly Presentacion[]): Presentacion[] {
  return pres
    .filter((p) => !p.es_base && p.activa && Number(p.factor_a_base) > 1)
    .sort((a, b) => Number(a.factor_a_base) - Number(b.factor_a_base));
}

/** Número sin ruido binario, hasta 6 decimales. */
const limpio = (n: number) => String(Number(n.toFixed(6)));

/**
 * Cantidad de la línea como en el punto de venta (Rodrigo, 2026-10-24): si el producto tiene caja, al
 * entrar a la celda se abre «Cantidad para unidades distintas» (unidades o cajas, con la R.T.U.); si
 * no, se digita el número (o «5c»). Lo digitado se lleva a la unidad de la línea: si cuadra en cajas
 * queda en cajas; si no (50 UN con caja de 8), la línea pasa a la unidad base y el precio se convierte.
 */
function CeldaCantidadCompra({
  empresaId,
  linea: l,
  numero,
  editable,
  unidadFija,
  alCambiar,
  alSiguiente,
  alTecla,
}: {
  empresaId: string;
  linea: LineaCompra;
  numero: number;
  editable: boolean;
  /** Línea de una guía o de la OC: su unidad no cambia (la cantidad debe cuadrar en ella). */
  unidadFija: boolean;
  alCambiar: (c: Partial<LineaCompra>) => void;
  alSiguiente: () => void;
  alTecla: (e: KeyboardEvent<HTMLInputElement>) => void;
}) {
  const q = useProducto(empresaId, l.productoId);
  const pres = (q.data?.presentaciones ?? []) as Presentacion[];
  const unidadBase = q.data?.producto.unidad_base_codigo ?? 'UN';
  const base = pres.find((p) => p.es_base) ?? null;
  const cajas = cajasDeCompra(pres);
  const conRtu = editable && cajas.length > 0 && !!base;
  const [abierto, setAbierto] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [texto, setTexto] = useState<string | null>(null);
  const callado = useRef(false);
  const celda = useRef<HTMLInputElement>(null);
  // Como el punto de venta: si el producto (con caja) termina de cargar estando en la celda, se abre.
  useEffect(() => {
    if (conRtu && !callado.current && document.activeElement === celda.current) setAbierto(true);
  }, [conRtu]);

  /** Lleva una cantidad en unidad base a la línea (en cajas si cuadra; si no, en unidad base). */
  const aplicarBase = (cantidadBase: number): boolean => {
    const enLinea = cantidadBase / l.factor;
    if (l.factor === 1 || Math.abs(enLinea - Math.round(enLinea)) < 1e-9 || !base) {
      alCambiar({ cantidad: limpio(enLinea) });
      return true;
    }
    if (unidadFija) {
      setError(
        `${decimal(limpio(cantidadBase))} ${unidadBase} no son ${l.unidad} completas (×${decimal(String(l.factor))})`,
      );
      return false;
    }
    const precio = num(l.precio)
      ? String(Math.round((num(l.precio) / l.factor) * 100) / 100)
      : l.precio;
    alCambiar({
      cantidad: limpio(cantidadBase),
      presentacionId: base.id,
      unidad: base.codigo,
      factor: 1,
      precio,
    });
    return true;
  };
  /** Sin caja: número en la unidad de la línea, o «5c» si existe esa presentación. */
  const confirmar = (): boolean => {
    if (texto === null) return true;
    if (!texto.trim()) {
      alCambiar({ cantidad: '' });
      setTexto(null);
      return true;
    }
    const simple = /^\s*\d+(?:[.,]\d+)?\s*$/.test(texto);
    if (simple) {
      alCambiar({ cantidad: texto.trim().replace(',', '.') });
      setTexto(null);
      setError(null);
      return true;
    }
    const leida = leerCantidad(texto, pres, unidadBase);
    if (!leida.ok) {
      setError(leida.error);
      return false;
    }
    if (!aplicarBase(Number(leida.base))) return false;
    setTexto(null);
    setError(null);
    return true;
  };
  const equivalencia =
    l.factor !== 1 && num(l.cantidad) > 0
      ? `${decimal(l.cantidad)} ${l.unidad} = ${decimal(limpio(num(l.cantidad) * l.factor))} ${unidadBase}`
      : null;
  return (
    <>
      <span className="cantidad-con-unidad">
        <input
          ref={celda}
          className="celda-num"
          inputMode="decimal"
          data-clave={l.clave}
          data-col="cantidad"
          aria-label={`cantidad línea ${numero}`}
          aria-invalid={error ? true : undefined}
          readOnly={!editable}
          value={texto ?? l.cantidad}
          onFocus={(e) => {
            e.target.select();
            if (conRtu && !callado.current) setAbierto(true);
          }}
          onBlur={() => {
            callado.current = false;
            // Con caja, el diálogo decide la cantidad (como en el punto de venta): no se confirma a medias.
            if (conRtu) return;
            if (!confirmar()) {
              setTexto(null);
              setError(null);
            }
          }}
          onChange={(e) => {
            setTexto(e.target.value);
            setError(null);
            if (conRtu) setAbierto(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !confirmar()) {
              e.preventDefault();
              return;
            }
            alTecla(e);
          }}
        />
        <span className="unidad">{l.unidad}</span>
      </span>
      {(error ?? equivalencia) && (
        <span
          className={`ayuda-cantidad ${error ? 'texto-malo' : ''}`}
          role={error ? 'alert' : undefined}
        >
          {error ?? equivalencia}
        </span>
      )}
      {abierto && conRtu && (
        <CantidadRtu
          sku={l.sku}
          nombre={l.nombre}
          unidad={unidadBase}
          presentaciones={pres}
          cajas={cajas}
          inicial={texto ?? (num(l.cantidad) > 0 ? limpio(num(l.cantidad) * l.factor) : '')}
          reemplazar={texto !== null}
          multiplo={null}
          disponible={null}
          alAceptar={(b) => {
            setAbierto(false);
            setTexto(null);
            setError(null);
            // El foco vuelve a la celda al cerrar el diálogo: que no lo reabra.
            callado.current = true;
            if (aplicarBase(Number(b))) setTimeout(alSiguiente, 0);
          }}
          alCancelar={() => {
            setAbierto(false);
            setTexto(null);
            // El foco vuelve a la celda sin reabrir el diálogo.
            callado.current = true;
          }}
        />
      )}
    </>
  );
}

export function EditorCompra({
  tipoInicial,
  ordenId,
  recepcionId,
  proveedorId,
  corrigeId,
  alCreado,
}: {
  tipoInicial: TipoDocCompra;
  /**
   * 0131: el ingreso sin factura aceptado que se CORRIGE. Llega con todo lo del original; productos y
   * cantidades quedan fijos (lo que entró al stock no se mueve) y se graba como el mismo número.
   */
  corrigeId?: string | undefined;
  /** Desde la ficha del proveedor: viene puesto. */
  proveedorId?: string | undefined;
  /** Guía contra una OC: trae sus líneas. */
  ordenId?: string | undefined;
  /** Factura desde una guía ya recibida: trae sus líneas pendientes de facturar. */
  recepcionId?: string | undefined;
  alCreado: (tipo: TipoDocCompra, id: string, titulo: string) => void;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const bodegas = useBodegas(empresaId);
  const accion = useAccionCompras(empresaId);
  const [tipo, setTipo] = useState<TipoDocCompra>(tipoInicial);
  const [proveedor, setProveedor] = useState<Proveedor | null>(null);
  const [folio, setFolio] = useState('');
  const [tipoSii, setTipoSii] = useState(33);
  const [emision, setEmision] = useState(hoy());
  const [bodegaId, setBodegaId] = useState('');
  const [llegada, setLlegada] = useState<Llegada>(recepcionId ? 'GUIAS' : 'AHORA');
  const [orden, setOrden] = useState<{ id: string; numero: string } | null>(null);
  const [lineas, setLineas] = useState<LineaCompra[]>([]);
  const [codigo, setCodigo] = useState('');
  const [fleteGlobal, setFleteGlobal] = useState('');
  const [reparto, setReparto] = useState<Reparto>('PROVEEDOR');
  const [ivaManual, setIvaManual] = useState<string | null>(null);
  /** Impuestos adicionales ajustados a mano al monto de la factura, por código. */
  const [adicManual, setAdicManual] = useState<Record<string, string>>({});
  const [adicionalCodigo, setAdicionalCodigo] = useState('');
  const [adicional, setAdicional] = useState('');
  const [observacion, setObservacion] = useState('');
  /** Ingreso sin factura: quién emitió el documento de origen (p. ej. Andina a Costa Delivery). */
  const [origen, setOrigen] = useState<Proveedor | null>(null);
  const [dialogo, setDialogo] = useState<
    | null
    | { tipo: 'proveedor' }
    | { tipo: 'origen' }
    | { tipo: 'producto'; texto: string }
    | { tipo: 'nuevoProducto'; texto: string }
    | { tipo: 'guias' }
  >(null);
  const [error, setError] = useState<unknown>(null);
  const [resultado, setResultado] = useState<string | null>(null);
  const filaCodigo = useRef<HTMLInputElement>(null);
  const grilla = useRef<HTMLTableSectionElement>(null);
  /** Factura e ingreso sin factura llevan precio, flete, impuestos, costo y condición de pago. */
  const esFactura = tipo === 'FCC' || tipo === 'ISF';
  // 0131 · CORREGIR un ingreso sin factura (pedido de Rodrigo, 2026-10-31): Daniela cambia el precio,
  // el descuento, el flete o los impuestos; el original queda anulado y el corregido cruza con la
  // misma recepción.
  const corregido = useQuery({
    queryKey: ['emp', empresaId, 'compras', 'corrige', corrigeId ?? ''],
    queryFn: () => api.get<FacturaLeida>(`/compras/facturas/${corrigeId}`),
    enabled: !!corrigeId,
  });
  const recepcionCorregida = corregido.data?.asignaciones[0]?.recepcion_id;
  const bodegaCorregida = useQuery({
    queryKey: ['emp', empresaId, 'compras', 'rec-corrige', recepcionCorregida ?? ''],
    queryFn: () =>
      api.get<{ recepcion: { ubicacion_id: string } | null }>(
        `/compras/recepciones/${recepcionCorregida}`,
      ),
    enabled: !!recepcionCorregida,
  });
  const [motivoCorreccion, setMotivoCorreccion] = useState('Corrección de precio o descuento');

  useEffect(() => {
    if (!bodegaId && bodegas.lista[0]) setBodegaId(bodegas.lista[0].id);
  }, [bodegas.lista, bodegaId]);

  // --- datos del proveedor: cómo reparte el flete y sus costos logísticos
  const estrategiaProv = useQuery({
    queryKey: ['emp', empresaId, 'compras', 'estrategia-flete', proveedor?.id ?? ''],
    queryFn: () =>
      api.get<{ estrategia: { estrategia: string; codigo: string } | null }>(
        '/compras/costos-logisticos/estrategia',
        { query: { empresaId, proveedorEntidadId: proveedor!.id } },
      ),
    enabled: !!proveedor && esFactura,
  });
  const costosLog = useQuery({
    queryKey: ['emp', empresaId, 'compras', 'costos-logisticos', proveedor?.id ?? ''],
    queryFn: () =>
      api.get<CostoLogistico[]>('/compras/costos-logisticos', {
        query: { empresaId, proveedorEntidadId: proveedor!.id },
      }),
    enabled: !!proveedor && esFactura,
  });
  const costos = useMemo(
    () =>
      new Map(
        (costosLog.data ?? [])
          .filter((c) => c.montoBase !== null)
          .map((c) => [c.productoId, Number(c.montoBase)]),
      ),
    [costosLog.data],
  );
  const guias = useQuery({
    queryKey: ['emp', empresaId, 'compras', 'por-facturar', proveedor?.id ?? ''],
    queryFn: () =>
      api.get<GuiaPorFacturar[]>('/compras/recepciones/por-facturar', {
        query: { empresaId, proveedorEntidadId: proveedor!.id },
      }),
    enabled: !!proveedor && tipo === 'FCC',
  });

  // --- desde una OC (guía contra OC) o desde una guía (factura que cruza)
  const ordenQ = useQuery({
    queryKey: ['emp', empresaId, 'compras', 'oc-editor', ordenId ?? ''],
    queryFn: () => api.get<OrdenLeida>(`/compras/ordenes/${ordenId}`),
    enabled: !!ordenId,
  });
  useEffect(() => {
    const o = ordenQ.data;
    if (!o || lineas.length) return;
    setTipo('GRC');
    setOrden({ id: o.orden.id, numero: o.numeroVisible });
    setProveedor({
      id: o.orden.proveedor_entidad_id,
      rut: o.orden.proveedor_rut?.split('-')[0] ?? null,
      dv: o.orden.proveedor_rut?.split('-')[1] ?? null,
      razonSocial: o.orden.proveedor_razon_social,
      plazoDias: o.orden.plazo_dias,
    });
    setBodegaId(o.orden.ubicacion_destino_id);
    const recibido = new Map(o.cumplimiento.lineas.map((x) => [x.numero, Number(x.recibidaBase)]));
    setLineas(
      o.lineas
        .map((l) => {
          const factor = Number(l.factorABase) || 1;
          const pendiente = Math.max(
            0,
            Number(l.cantidad) - (recibido.get(l.numero) ?? 0) / factor,
          );
          return {
            clave: nuevaClave(),
            productoId: l.productoId,
            presentacionId: l.presentacionId,
            sku: l.sku,
            nombre: l.descripcion,
            unidad: l.presentacionCodigo,
            factor,
            cantidad: String(Number(pendiente.toFixed(4))),
            rechazada: '',
            motivo: '',
            precio: String(Number(l.precioUnitario)),
            desc: '',
            flete: '',
            ordenLineaNumero: l.numero,
          };
        })
        .filter((l) => Number(l.cantidad) > 0),
    );
  }, [ordenQ.data]);

  const traerGuias = (elegidas: GuiaPorFacturar[]) => {
    setLineas((ls) => [
      ...ls,
      ...elegidas.flatMap((g) =>
        g.lineas
          .filter((l) => !ls.some((x) => x.recepcionLineaId === l.recepcionLineaId))
          .map((l) => ({
            clave: nuevaClave(),
            productoId: l.productoId,
            presentacionId: l.presentacionId,
            sku: l.sku,
            nombre: l.descripcion,
            unidad: l.presentacionCodigo,
            factor: Number(l.factorABase) || 1,
            cantidad: l.pendiente,
            rechazada: '',
            motivo: '',
            precio: l.precioOc ?? '',
            desc: '',
            flete: '',
            recepcionLineaId: l.recepcionLineaId,
            ...(l.ordenLineaNumero ? { ordenLineaNumero: l.ordenLineaNumero } : {}),
          })),
      ),
    ]);
    setLlegada('GUIAS');
  };
  // Factura abierta desde una guía: se trae sola.
  useEffect(() => {
    if (!recepcionId || !guias.data || lineas.length) return;
    const g = guias.data.find((x) => x.recepcionId === recepcionId);
    if (g) traerGuias([g]);
  }, [guias.data, recepcionId]);
  const recepcionInicial = useQuery({
    queryKey: ['emp', empresaId, 'compras', 'rec-editor', recepcionId ?? ''],
    queryFn: () =>
      api.get<{ borrador: { proveedor_entidad_id: string } }>(
        `/compras/recepciones/${recepcionId}`,
      ),
    enabled: !!recepcionId && !proveedor,
  });
  const proveedorInicialId =
    recepcionInicial.data?.borrador.proveedor_entidad_id ??
    proveedorId ??
    corregido.data?.documento.proveedor_entidad_id ??
    undefined;
  const fichaProv = useQuery({
    queryKey: ['entidad-compra', empresaId, proveedorInicialId ?? ''],
    queryFn: () =>
      api.get<{
        entidad: { id: string; rut: string | null; dv: string | null; razon_social: string };
        empresas: Array<{ empresa_id: string; plazo_dias: number | null }>;
      }>(`/entidades/${proveedorInicialId}`),
    enabled: !!proveedorInicialId && !proveedor,
  });
  useEffect(() => {
    const e = fichaProv.data?.entidad;
    if (e && !proveedor)
      setProveedor({
        id: e.id,
        rut: e.rut,
        dv: e.dv,
        razonSocial: e.razon_social,
        plazoDias:
          fichaProv.data?.empresas?.find((x) => x.empresa_id === empresaId)?.plazo_dias ?? null,
      });
  }, [fichaProv.data]);

  // El ingreso que se corrige: sus líneas (con la línea recibida de cada una), su flete, su número, su
  // documento de origen y su bodega. Los impuestos se recalculan con los precios nuevos.
  useEffect(() => {
    const f = corregido.data;
    if (!f || lineas.length) return;
    const d = f.documento;
    setTipo('ISF');
    setFolio(d.numero_externo ?? '');
    setEmision(d.fecha_emision.slice(0, 10));
    const ref = f.referencias.find((r) => r.tipo === 'DOCUMENTO')?.razon ?? '';
    const m = /^(.*?) N° \S+ de (.+) \((\d+)-([\dkK])\)$/.exec(ref);
    if (m) {
      setTipoSii(m[1] === 'Factura exenta' ? 34 : m[1] === 'Factura de compra' ? 46 : 33);
      setOrigen({ id: '', rut: m[3]!, dv: m[4]!, razonSocial: m[2]!, plazoDias: null });
    }
    const fleteDe = new Map<number, number>();
    let fleteTotal = 0;
    for (const c of f.cargos) {
      if (c.tipo !== 'FLETE') continue;
      const n = /\(línea (\d+)\)$/.exec(c.descripcion);
      if (n) fleteDe.set(Number(n[1]), (fleteDe.get(Number(n[1])) ?? 0) + Number(c.monto));
      else fleteTotal += Number(c.monto);
    }
    setFleteGlobal(fleteTotal ? String(fleteTotal) : '');
    const recibida = new Map(
      f.asignaciones.map((a) => [a.documento_linea_id, a.recepcion_linea_id]),
    );
    setLineas(
      f.lineas.map((l) => ({
        clave: nuevaClave(),
        productoId: l.producto_id ?? '',
        presentacionId: l.presentacion_id ?? '',
        sku: l.sku ?? '',
        nombre: l.descripcion,
        unidad: l.unidad ?? l.presentacion_codigo ?? '',
        factor: Number(l.factor_a_base) || 1,
        cantidad: String(Number(l.cantidad)),
        rechazada: '',
        motivo: '',
        precio: String(Number(l.precio_unitario)),
        desc: Number(l.descuento) > 0 ? `$${Math.round(Number(l.descuento))}` : '',
        flete: fleteDe.has(l.numero) ? String(fleteDe.get(l.numero)) : '',
        ...(recibida.get(l.id) ? { recepcionLineaId: recibida.get(l.id)! } : {}),
      })),
    );
  }, [corregido.data]);
  useEffect(() => {
    const u = bodegaCorregida.data?.recepcion?.ubicacion_id;
    if (u) setBodegaId(u);
  }, [bodegaCorregida.data]);

  // --- cálculos
  const exento = tipoSii === 34;
  const estrategia: Exclude<Reparto, 'PROVEEDOR'> | null =
    reparto === 'PROVEEDOR'
      ? ((estrategiaProv.data?.estrategia?.estrategia as
          Exclude<Reparto, 'PROVEEDOR'> | undefined) ?? 'FLETE_POR_VALOR')
      : reparto;
  const global = esFactura ? redondo(num(fleteGlobal)) : 0;
  const fletes = esFactura ? repartirFleteLineas(lineas, global, estrategia, costos) : [];
  const netos = lineas.map(netoLinea);
  const sumaLineas = netos.reduce((a, b) => a + b, 0);
  const fletesLinea = esFactura ? lineas.reduce((s, l) => s + redondo(num(l.flete)), 0) : 0;
  const neto = sumaLineas + fletesLinea + global;
  const ivaCalculado = tipo === 'OCC' || exento ? 0 : redondo(neto * 0.19);
  const iva = ivaManual === null ? ivaCalculado : redondo(num(ivaManual));
  // Impuestos adicionales (pedido de Rodrigo, 2026-10-26): cada uno se calcula como el IVA, pero solo
  // sobre las líneas cuyos productos lo llevan según su ficha (ILA20C a la cerveza, ILA31L al licor…),
  // nunca sobre el flete, y se imputa a cada línea por su neto. Así el costo bruto de cada producto
  // lleva SU impuesto y no uno repartido entre toda la factura.
  const productosLineas = useMemo(
    () => [...new Set(lineas.map((l) => l.productoId))].sort(),
    [lineas],
  );
  const impuestosProd = useQuery({
    queryKey: [
      'emp',
      empresaId,
      'compras',
      'impuestos-producto',
      emision,
      productosLineas.join(','),
    ],
    queryFn: () =>
      api.get<ImpuestosDeProducto[]>('/compras/impuestos-producto', {
        query: { empresaId, fecha: emision, productoIds: productosLineas.join(',') },
      }),
    enabled: esFactura && productosLineas.length > 0 && /^\d{4}-\d{2}-\d{2}$/.test(emision),
  });
  const adicionalesDe = new Map(
    (impuestosProd.data ?? []).map((x) => [
      x.productoId,
      x.impuestos.filter((i) => i.tipo !== 'iva' && i.tipo !== 'exento'),
    ]),
  );
  const sinTasa = (impuestosProd.data ?? []).filter((x) => x.error);
  const porCodigo = new Map<string, { tasa: string; lineas: number[] }>();
  if (esFactura && !exento)
    lineas.forEach((l, i) => {
      for (const imp of adicionalesDe.get(l.productoId) ?? []) {
        const c = porCodigo.get(imp.codigo) ?? { tasa: imp.tasa, lineas: [] };
        c.lineas.push(i);
        porCodigo.set(imp.codigo, c);
      }
    });
  const adicionales: AdicionalCalculado[] = [...porCodigo]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([codigo, c]) => {
      const base = c.lineas.reduce((s, i) => s + netos[i]!, 0);
      const calculado = redondo(base * Number(c.tasa));
      const manual = adicManual[codigo];
      const monto = manual !== undefined ? redondo(num(manual)) : calculado;
      return {
        codigo,
        tasa: c.tasa,
        base,
        calculado,
        monto,
        lineas: c.lineas,
        porLinea: prorratear(
          monto,
          c.lineas.map((i) => netos[i]!),
        ),
      };
    });
  /** Lo adicional que le toca a cada línea (para su costo bruto). */
  const adicionalDeLinea = lineas.map((_, i) =>
    adicionales.reduce((s, a) => {
      const k = a.lineas.indexOf(i);
      return k < 0 ? s : s + a.porLinea[k]!;
    }, 0),
  );
  // Otro impuesto que el maestro no conoce para estos productos: va solo en la cabecera.
  const otroAdic = esFactura ? redondo(num(adicional)) : 0;
  const adic = adicionales.reduce((s, a) => s + a.monto, 0) + otroAdic;
  const total = neto + (tipo === 'OCC' ? 0 : iva) + adic;
  const faltanCostos =
    esFactura &&
    global > 0 &&
    estrategia === 'FLETE_POR_COSTO_LOGISTICO' &&
    lineas.some((l) => !costos.get(l.productoId));
  const venceProveedor = useMemo(() => {
    if (!proveedor?.plazoDias) return emision;
    const d = new Date(`${emision}T12:00:00`);
    d.setDate(d.getDate() + proveedor.plazoDias);
    return d.toISOString().slice(0, 10);
  }, [emision, proveedor]);
  // Condición de pago de la factura (0111, Rodrigo 2026-10-24): parte del plazo del proveedor y se
  // puede cambiar; queda registrada con la factura y Tesorería paga según ese vencimiento.
  const [condicionPago, setCondicionPago] = useState<'CONTADO' | 'CREDITO' | null>(null);
  const [venceManual, setVenceManual] = useState<string | null>(null);
  const [formaPago, setFormaPago] = useState<
    'TRANSFERENCIA' | 'CHEQUE' | 'EFECTIVO' | 'TARJETA' | 'OTRO'
  >('TRANSFERENCIA');
  const condicion = condicionPago ?? (venceProveedor > emision ? 'CREDITO' : 'CONTADO');
  const vence = condicion === 'CONTADO' ? emision : (venceManual ?? venceProveedor);

  // --- edición de líneas
  const cambiar = (clave: string, campos: Partial<LineaCompra>) =>
    setLineas((ls) => ls.map((l) => (l.clave === clave ? { ...l, ...campos } : l)));
  const quitar = (clave: string) => setLineas((ls) => ls.filter((l) => l.clave !== clave));

  /** Precio sugerido: el último costo de compra del producto, en la presentación de la línea. */
  const sugerirPrecio = async (productoId: string, factor: number): Promise<string> => {
    try {
      const h = await api.get<CompraDeProducto[]>('/compras/costos', {
        query: { empresaId, productoId },
      });
      const u = h[0];
      return u ? String(Math.round(Number(u.costoBase) * factor * 100) / 100) : '';
    } catch {
      return '';
    }
  };

  const agregarProducto = async (
    producto: { id: string; sku: string; nombre: string },
    presentacion: Presentacion,
  ) => {
    const factor = Number(presentacion.factor_a_base) || 1;
    const clave = nuevaClave();
    setLineas((ls) => [
      ...ls,
      {
        clave,
        productoId: producto.id,
        presentacionId: presentacion.id,
        sku: producto.sku,
        nombre: producto.nombre,
        unidad: presentacion.codigo,
        factor,
        cantidad: '',
        rechazada: '',
        motivo: '',
        precio: '',
        desc: '',
        flete: '',
      },
    ]);
    setCodigo('');
    const precio = await sugerirPrecio(producto.id, factor);
    if (precio)
      setLineas((ls) => ls.map((l) => (l.clave === clave && !l.precio ? { ...l, precio } : l)));
    // Foco a la cantidad de la línea nueva.
    setTimeout(() => {
      grilla.current
        ?.querySelector<HTMLInputElement>(`[data-clave="${clave}"][data-col="cantidad"]`)
        ?.focus();
    }, 30);
  };

  /** En compras se compra por caja: si el producto tiene caja, la línea parte en caja. */
  const presentacionDeCompra = async (productoId: string, base: Presentacion) => {
    try {
      const pres = await api.get<Presentacion[]>(`/productos/${productoId}/presentaciones`);
      const cajas = pres
        .filter((p) => p.activa && !p.es_base)
        .sort((a, b) => Number(b.factor_a_base) - Number(a.factor_a_base));
      return cajas[0] ?? base;
    } catch {
      return base;
    }
  };

  const ingresarCodigo = async (texto: string) => {
    const t = texto.trim();
    if (!t) {
      setDialogo({ tipo: 'producto', texto: '' });
      return;
    }
    const exacto = await resolverCodigo(empresaId, t).catch(() => null);
    if (exacto) {
      const p = await presentacionDeCompra(exacto.producto.id, exacto.base);
      await agregarProducto(exacto.producto, p);
    } else setDialogo({ tipo: 'producto', texto: t });
  };

  // Columnas editables por documento, en el orden en que Enter avanza.
  const columnas: string[] =
    tipo === 'OCC'
      ? ['cantidad', 'precio', 'desc']
      : tipo === 'GRC'
        ? ['cantidad', 'rechazada', 'motivo']
        : ['cantidad', 'precio', 'desc', 'flete'];
  const mover = (fila: number, c: string) => {
    const l = lineas[fila];
    const destino = l
      ? grilla.current?.querySelector<HTMLInputElement>(
          `[data-clave="${l.clave}"][data-col="${c}"]`,
        )
      : null;
    if (destino) {
      destino.focus();
      destino.select();
    } else filaCodigo.current?.focus();
  };
  /** Enter: la siguiente columna editable de la fila (en la guía el motivo solo si algo se rechazó). */
  const avanzar = (i: number, col: string) => {
    const k = columnas.indexOf(col);
    const siguiente = columnas
      .slice(k + 1)
      .find((c) => c !== 'motivo' || num(lineas[i]?.rechazada ?? '') > 0);
    if (siguiente) mover(i, siguiente);
    else filaCodigo.current?.focus();
  };
  const teclaCelda = (e: KeyboardEvent<HTMLInputElement>, i: number, col: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      avanzar(i, col);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      mover(i + 1, col);
    } else if (e.key === 'ArrowUp' && i > 0) {
      e.preventDefault();
      mover(i - 1, col);
    } else if (e.key === 'Delete' && e.ctrlKey) {
      e.preventDefault();
      if (!corrigeId) quitar(lineas[i]!.clave);
    }
  };

  // --- validación y grabar
  // La factura lleva su folio (el ingreso sin factura, el de su documento de origen); la guía del
  // proveedor, si vino con una (si no, se recibe igual).
  const faltaFolio =
    (esFactura && !/^\d{1,18}$/.test(folio.trim())) ||
    (tipo === 'GRC' && !!folio.trim() && !/^\d{1,18}$/.test(folio.trim()));
  const lineasMalas = lineas.filter((l) => {
    if (!(num(l.cantidad) > 0)) return true;
    if (tipo !== 'GRC' && !(num(l.precio) > 0)) return true;
    if (tipo === 'GRC') {
      const r = num(l.rechazada);
      if (r < 0 || r > num(l.cantidad)) return true;
      if (r > 0 && l.motivo.trim().length < 3) return true;
    }
    return false;
  });
  const mezclaGuias =
    tipo === 'FCC' && llegada === 'GUIAS' && lineas.some((l) => !l.recepcionLineaId);
  const puedeGrabar =
    !!proveedor &&
    !!bodegaId &&
    lineas.length > 0 &&
    lineasMalas.length === 0 &&
    !faltaFolio &&
    !mezclaGuias &&
    (tipo !== 'ISF' || !!origen) &&
    (!corrigeId || (!!corregido.data && motivoCorreccion.trim().length >= 10)) &&
    (!esFactura ||
      (!!proveedor.rut &&
        !impuestosProd.isLoading &&
        (!otroAdic ||
          (/^[A-Z0-9_]{2,40}$/.test(adicionalCodigo) && !porCodigo.has(adicionalCodigo))))) &&
    !accion.isPending;

  const decimalApi = (t: string, dec: number) => {
    const n = num(t);
    return String(Number(n.toFixed(dec)));
  };

  const cuerpoFactura = () => {
    const lineasApi = lineas.map((l, i) => {
      const d = descuentoLinea(l);
      return {
        descripcion: l.nombre.slice(0, 300),
        productoId: l.productoId,
        presentacionId: l.presentacionId,
        unidad: l.unidad.slice(0, 20),
        cantidad: decimalApi(l.cantidad, 6),
        precioUnitario: decimalApi(l.precio, 6),
        ...(d ? { descuento: String(d) } : {}),
        neto: String(netos[i]),
        ...(exento ? { exenta: true } : {}),
        // El ILA de la línea, imputado a su producto (la cabecera lleva la suma por código).
        ...(adicionales.some((a) => a.lineas.includes(i))
          ? {
              impuestos: adicionales.flatMap((a) => {
                const k = a.lineas.indexOf(i);
                return k < 0 || a.porLinea[k] === 0
                  ? []
                  : [
                      {
                        codigo: a.codigo,
                        tipo: 'adicional',
                        tasa: a.tasa,
                        monto: String(a.porLinea[k]),
                      },
                    ];
              }),
            }
          : {}),
      };
    });
    // Un cargo de flete por cada línea que lo trae, y uno por el flete global.
    const cargos = [
      ...lineas.flatMap((l, i) =>
        num(l.flete) > 0
          ? [
              {
                linea: i + 1,
                cargo: {
                  tipo: 'FLETE' as const,
                  descripcion: `Flete ${l.sku} (línea ${i + 1})`.slice(0, 200),
                  monto: String(redondo(num(l.flete))),
                  ...(exento ? { exento: true } : {}),
                },
              },
            ]
          : [],
      ),
      ...(global > 0
        ? [
            {
              linea: null,
              cargo: {
                tipo: 'FLETE' as const,
                descripcion: 'Flete',
                monto: String(global),
                ...(exento ? { exento: true } : {}),
              },
            },
          ]
        : []),
    ];
    const impuestos = [
      ...(iva ? [{ codigo: 'IVA', tipo: 'iva', tasa: '0.19', monto: String(iva) }] : []),
      ...adicionales
        .filter((a) => a.monto > 0)
        .map((a) => ({
          codigo: a.codigo,
          tipo: 'adicional',
          tasa: a.tasa,
          monto: String(a.monto),
        })),
      ...(otroAdic
        ? [{ codigo: adicionalCodigo, tipo: 'adicional', monto: String(otroAdic) }]
        : []),
    ];
    const recepciones = [
      ...new Set(
        lineas
          .map(
            (l) =>
              guias.data?.find((g) =>
                g.lineas.some((x) => x.recepcionLineaId === l.recepcionLineaId),
              )?.recepcionId,
          )
          .filter((x): x is string => !!x),
      ),
    ];
    // El ingreso sin factura es un documento NO tributario del proveedor a quien se le paga, con el
    // número del documento de origen; quién lo emitió queda en su referencia.
    const referencias = [
      ...recepciones.map((r) => ({ tipo: 'RECEPCION', recepcionId: r })),
      ...(tipo === 'ISF' && origen
        ? [
            {
              tipo: 'DOCUMENTO',
              folio: folio.trim(),
              razon:
                `${NOMBRE_TIPO_ORIGEN[tipoSii] ?? 'Documento'} N° ${folio.trim()} de ${origen.razonSocial} (${origen.rut ?? ''}-${origen.dv ?? ''})`.slice(
                  0,
                  200,
                ),
            },
          ]
        : []),
    ];
    const factura = {
      empresaId,
      identidad:
        tipo === 'ISF'
          ? {
              clase: 'NO_TRIBUTARIA',
              claseExterna: 'OTRO',
              numero: folio.trim(),
              rutEmisor: `${proveedor!.rut}-${proveedor!.dv ?? ''}`,
            }
          : {
              clase: 'TRIBUTARIA',
              tipoSii,
              folio: Number(folio.trim()),
              rutEmisor: `${proveedor!.rut}-${proveedor!.dv ?? ''}`,
              rutReceptor: `${empresa!.rut}-${empresa!.dv}`,
            },
      proveedorEntidadId: proveedor!.id,
      razonSocialEmisor: proveedor!.razonSocial,
      fechaEmision: emision,
      fechaRecepcion: hoy(),
      montos: {
        neto: exento ? '0' : String(neto),
        ...(exento ? { exento: String(neto) } : {}),
        iva: String(iva),
        impuestosAdicionales: String(adic),
        total: String(total),
      },
      lineas: lineasApi,
      ...(cargos.length ? { cargos: cargos.map((c) => c.cargo) } : {}),
      ...(impuestos.length ? { impuestos } : {}),
      ...(referencias.length ? { referencias } : {}),
    };
    const decisiones = cargos.map((c, i) =>
      c.linea !== null
        ? { cargoNumero: i + 1, lineas: [c.linea] }
        : reparto === 'PROVEEDOR'
          ? null
          : { cargoNumero: i + 1, estrategia: reparto },
    );
    return { factura, fletes: decisiones.filter((d) => d !== null) };
  };

  const grabar = () => {
    setError(null);
    setResultado(null);
    const listo = (t: TipoDocCompra, id: string, titulo: string, texto?: string) => {
      if (texto) setResultado(texto);
      alCreado(t, id, titulo);
    };
    if (tipo === 'OCC') {
      accion.mutate(
        {
          ruta: '/compras/ordenes',
          cuerpo: {
            empresaId,
            proveedorEntidadId: proveedor!.id,
            ubicacionDestinoId: bodegaId,
            ...(observacion.trim() ? { observaciones: observacion.trim() } : {}),
            lineas: lineas.map((l) => {
              const d = descuentoLinea(l);
              return {
                productoId: l.productoId,
                presentacionId: l.presentacionId,
                cantidad: decimalApi(l.cantidad, 6),
                precioUnitario: decimalApi(l.precio, 4),
                ...(d ? { descuento: String(d) } : {}),
              };
            }),
          },
        },
        {
          onSuccess: (r) => {
            const x = r as { ordenId: string; numeroVisible: string };
            listo('OCC', x.ordenId, x.numeroVisible);
          },
          onError: setError,
        },
      );
      return;
    }
    if (tipo === 'GRC') {
      const comun = {
        fechaNegocio: emision,
        ...(folio.trim() ? { guia: { tipo: 'GUIA_DESPACHO', folio: folio.trim() } } : {}),
        ...(observacion.trim() ? { observaciones: observacion.trim() } : {}),
      };
      const rechazo = (l: LineaCompra) =>
        num(l.rechazada) > 0
          ? { cantidadRechazada: decimalApi(l.rechazada, 4), motivoRechazo: l.motivo.trim() }
          : {};
      const cuerpo = orden
        ? {
            modalidad: 'CONTRA_OC',
            ordenId: orden.id,
            ubicacionId: bodegaId,
            ...comun,
            lineas: lineas.map((l) => ({
              ordenLineaNumero: l.ordenLineaNumero!,
              cantidadRecibida: decimalApi(l.cantidad, 4),
              ...rechazo(l),
            })),
          }
        : {
            modalidad: 'SIN_OC',
            empresaId,
            proveedorEntidadId: proveedor!.id,
            ubicacionId: bodegaId,
            motivoSinOc: folio.trim()
              ? `Guía de despacho N° ${folio.trim()} del proveedor (sin orden de compra)`
              : 'Mercadería recibida sin guía ni orden de compra',
            ...comun,
            lineas: lineas.map((l) => ({
              productoId: l.productoId,
              presentacionId: l.presentacionId,
              cantidadRecibida: decimalApi(l.cantidad, 4),
              ...rechazo(l),
            })),
          };
      accion.mutate(
        { ruta: '/compras/recepciones', cuerpo },
        {
          onSuccess: (r) => {
            const x = r as { recepcionId: string; version: number };
            accion.mutate(
              {
                ruta: `/compras/recepciones/${x.recepcionId}/confirmar`,
                cuerpo: { version: x.version ?? 1 },
              },
              {
                onSuccess: (c) => {
                  const y = c as { numeroVisible?: string };
                  listo('GRC', x.recepcionId, y.numeroVisible ?? 'Recepción');
                },
                // Quedó guardada sin confirmar: se abre para confirmarla ahí.
                onError: () => listo('GRC', x.recepcionId, 'Recepción'),
              },
            );
          },
          onError: setError,
        },
      );
      return;
    }
    // Factura
    const { factura, fletes: decisiones } = cuerpoFactura();
    const fin = async (r: unknown) => {
      const x = r as { documentoId: string; aceptada?: boolean };
      // La condición de pago queda con la factura (si falla, se corrige en su ficha).
      await api
        .post(
          `/compras/facturas/${x.documentoId}/condicion-pago`,
          { condicion, fechaVencimiento: vence, formaPago },
          { idempotencia: `cp:${x.documentoId}:${condicion}:${vence}:${formaPago}` },
        )
        .catch(() => undefined);
      listo(tipo, x.documentoId, `${tipo} ${folio.trim()}`);
    };
    if (corrigeId) {
      accion.mutate(
        {
          ruta: `/compras/facturas/${corrigeId}/corregir-ingreso`,
          cuerpo: {
            motivo: motivoCorreccion.trim(),
            factura,
            ...(decisiones.length ? { fletes: decisiones } : {}),
          },
        },
        { onSuccess: fin, onError: setError },
      );
      return;
    }
    if (llegada === 'AHORA')
      accion.mutate(
        {
          ruta: '/compras/facturas/directa',
          cuerpo: {
            ubicacionId: bodegaId,
            factura,
            ...(decisiones.length ? { fletes: decisiones } : {}),
          },
        },
        { onSuccess: fin, onError: setError },
      );
    else if (llegada === 'GUIAS')
      accion.mutate(
        {
          ruta: '/compras/facturas/desde-guias',
          cuerpo: {
            factura,
            asignaciones: lineas.map((l, i) => ({
              documentoLineaNumero: i + 1,
              recepcionLineaId: l.recepcionLineaId!,
              cantidad: decimalApi(l.cantidad, 6),
            })),
            ...(decisiones.length ? { fletes: decisiones } : {}),
          },
        },
        { onSuccess: fin, onError: setError },
      );
    else
      accion.mutate(
        { ruta: '/compras/facturas', cuerpo: factura },
        { onSuccess: fin, onError: setError },
      );
  };

  // F2 proveedor · F3 producto · F9 grabar (como en Ventas).
  useEffect(() => {
    const t = (e: globalThis.KeyboardEvent) => {
      if (dialogo) return;
      if (e.key === 'F2') {
        e.preventDefault();
        if (!corrigeId) setDialogo({ tipo: 'proveedor' });
      } else if (e.key === 'F4' && tipo === 'ISF') {
        e.preventDefault();
        setDialogo({ tipo: 'origen' });
      } else if (e.key === 'F3') {
        e.preventDefault();
        if (!corrigeId) setDialogo({ tipo: 'producto', texto: codigo });
      } else if (e.key === 'F9' && puedeGrabar) {
        e.preventDefault();
        grabar();
      }
    };
    window.addEventListener('keydown', t);
    return () => window.removeEventListener('keydown', t);
  });

  const cambiarTipo = (t: TipoDocCompra) => {
    if (t === tipo) return;
    setTipo(t);
    // Las líneas de guías o de la OC no sirven para otro documento.
    setLineas((ls) => ls.filter((l) => !l.recepcionLineaId && !l.ordenLineaNumero));
    setOrden(null);
    if (t !== 'FCC') setLlegada('AHORA');
  };
  const permiso: Record<TipoDocCompra, string> = {
    OCC: 'compras.oc.crear',
    GRC: 'compras.recepcion.crear',
    FCC: 'compras.factura.crear',
    ISF: 'compras.factura.crear',
  };
  const etiquetaGrabar = corrigeId
    ? 'Grabar corrección · el stock no se mueve'
    : tipo === 'OCC'
      ? 'Grabar orden de compra'
      : tipo === 'GRC'
        ? 'Grabar guía · entra al stock'
        : tipo === 'ISF'
          ? 'Grabar ingreso · entra al stock'
          : llegada === 'AHORA'
            ? 'Grabar factura · entra al stock'
            : llegada === 'GUIAS'
              ? 'Grabar factura · cruzar con guías'
              : 'Grabar factura';
  const relleno = Math.max(0, 14 - lineas.length);
  const pendientesGuias = (guias.data ?? []).length;

  return (
    <div
      className="pantalla documento-compra c21-compra"
      data-pantalla="editor-compra"
      data-tipo={tipo}
    >
      {/* ------------------------------------------------ cabecera */}
      <section className="cab-compra" aria-label="Encabezado">
        <div className="c td">
          <span className="etiqueta-c21">Documento</span>
          <div className="segmentos" role="radiogroup" aria-label="Tipo de documento">
            {(['OCC', 'GRC', 'FCC', 'ISF'] as const).map((t) => (
              <button
                key={t}
                type="button"
                role="radio"
                aria-checked={tipo === t}
                className={tipo === t ? 'activo' : ''}
                title={NOMBRE_DOC_COMPRA[t]}
                disabled={!!corrigeId && t !== tipo}
                onClick={() => cambiarTipo(t)}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        <div className="c prov">
          <span className="etiqueta-c21">
            {tipo === 'ISF' ? 'Proveedor · se le paga a' : 'Proveedor'}{' '}
            <kbd className="tecla-campo">F2</kbd>
          </span>
          <button
            type="button"
            className="selector-cliente"
            aria-label="Elegir proveedor"
            disabled={!!corrigeId}
            onClick={() => setDialogo({ tipo: 'proveedor' })}
          >
            {proveedor ? (
              <>
                <span className="rut mono">
                  {proveedor.rut ? `${proveedor.rut}-${proveedor.dv ?? ''}` : ''}
                </span>{' '}
                <span className="razon">{proveedor.razonSocial}</span>
              </>
            ) : (
              'Buscar proveedor…'
            )}
          </button>
        </div>
        <label className="c num">
          <span className="etiqueta-c21">
            {tipo === 'OCC'
              ? 'Número OC'
              : tipo === 'GRC'
                ? 'N° guía del proveedor'
                : tipo === 'ISF'
                  ? 'N° doc. de origen'
                  : 'Folio factura'}
          </span>
          {tipo === 'OCC' ? (
            <span className="v apagado">al grabar</span>
          ) : (
            <input
              className="mono"
              aria-label={
                tipo === 'GRC'
                  ? 'Número de guía'
                  : tipo === 'ISF'
                    ? 'Número del documento de origen'
                    : 'Folio de la factura'
              }
              inputMode="numeric"
              readOnly={!!corrigeId}
              value={folio}
              onChange={(e) => setFolio(e.target.value.replace(/\D/g, ''))}
            />
          )}
        </label>
        {tipo === 'FCC' && (
          <label className="c tsii">
            <span className="etiqueta-c21">Tipo</span>
            <select
              aria-label="Tipo de factura"
              value={tipoSii}
              onChange={(e) => setTipoSii(Number(e.target.value))}
            >
              <option value={33}>33 Factura</option>
              <option value={34}>34 Exenta</option>
              <option value={46}>46 F. compra</option>
            </select>
          </label>
        )}
        <label className="c emi">
          <span className="etiqueta-c21">Emisión</span>
          <input
            type="date"
            aria-label="Fecha de emisión"
            value={emision}
            onChange={(e) => setEmision(e.target.value || hoy())}
          />
        </label>
        {esFactura && (
          <>
            <label className="c cond">
              <span className="etiqueta-c21">Pago</span>
              <select
                aria-label="Condición de pago"
                value={condicion}
                onChange={(e) => setCondicionPago(e.target.value as 'CONTADO' | 'CREDITO')}
              >
                <option value="CONTADO">Contado</option>
                <option value="CREDITO">Crédito</option>
              </select>
            </label>
            <label className="c vto">
              <span
                className="etiqueta-c21"
                title={`Plazo del proveedor: vence el ${fecha(venceProveedor)}`}
              >
                Vence
              </span>
              <input
                type="date"
                aria-label="Vencimiento"
                min={emision}
                disabled={condicion === 'CONTADO'}
                value={vence}
                onChange={(e) => setVenceManual(e.target.value || null)}
              />
            </label>
            <label className="c forma">
              <span className="etiqueta-c21">Forma de pago</span>
              <select
                aria-label="Forma de pago"
                value={formaPago}
                onChange={(e) => setFormaPago(e.target.value as typeof formaPago)}
              >
                <option value="TRANSFERENCIA">Transferencia</option>
                <option value="CHEQUE">Cheque</option>
                <option value="EFECTIVO">Efectivo</option>
                <option value="TARJETA">Tarjeta</option>
                <option value="OTRO">Otro</option>
              </select>
            </label>
          </>
        )}
        {/* «c bod» chocaba con la raíz de la app de bodega (.bod: alto de pantalla completa) */}
        <label className="c bodega-compra">
          <span className="etiqueta-c21">Bodega</span>
          <select
            aria-label="Bodega"
            value={bodegaId}
            disabled={!!orden || !!corrigeId}
            onChange={(e) => setBodegaId(e.target.value)}
          >
            {bodegas.lista.map((b) => (
              <option key={b.id} value={b.id}>
                {b.codigo} · {b.nombre}
              </option>
            ))}
          </select>
        </label>
        {orden && (
          <div className="c ref">
            <span className="etiqueta-c21">Contra la OC</span>
            <span className="v mono">{orden.numero}</span>
          </div>
        )}
      </section>

      {corrigeId && (
        <section className="franja-compra franja-correccion" aria-label="Corrección del ingreso">
          <span className="etiqueta-c21">Corrigiendo</span>
          <span className="mono">
            {corregido.data?.numeroVisible ?? (corregido.isError ? 'no se pudo leer' : '…')}
          </span>
          <label className="campo-origen motivo-correccion">
            <span className="etiqueta-c21">Qué se corrige</span>
            <input
              aria-label="Qué se corrige"
              value={motivoCorreccion}
              maxLength={500}
              onChange={(e) => setMotivoCorreccion(e.target.value)}
            />
          </label>
          <span className="ayuda-origen">
            Cambia el <b>precio</b>, el <b>descuento</b>, el <b>flete</b> o los impuestos: el
            original queda <b>anulado</b> y el corregido entra con el mismo número contra la misma
            recepción, así que <b>el stock no se mueve</b>. Para cambiar productos o cantidades,
            anula el ingreso y vuelve a ingresarlo.
          </span>
        </section>
      )}

      {tipo === 'ISF' && (
        <section className="franja-compra franja-origen" aria-label="Documento de origen">
          <span className="etiqueta-c21">Documento de origen</span>
          <label className="campo-origen">
            <span className="etiqueta-c21">Tipo</span>
            <select
              aria-label="Tipo del documento de origen"
              value={tipoSii}
              onChange={(e) => setTipoSii(Number(e.target.value))}
            >
              <option value={33}>33 Factura</option>
              <option value={34}>34 Exenta</option>
              <option value={46}>46 F. compra</option>
            </select>
          </label>
          <div className="campo-origen origen">
            <span className="etiqueta-c21">
              Emitido por <kbd className="tecla-campo">F4</kbd>
            </span>
            <button
              type="button"
              className="selector-cliente"
              aria-label="Elegir quién emitió el documento de origen"
              onClick={() => setDialogo({ tipo: 'origen' })}
            >
              {origen ? (
                <>
                  <span className="rut mono">
                    {origen.rut ? `${origen.rut}-${origen.dv ?? ''}` : ''}
                  </span>{' '}
                  <span className="razon">{origen.razonSocial}</span>
                </>
              ) : (
                'Quién emitió el documento…'
              )}
            </button>
          </div>
          <span className="ayuda-origen">
            Lo compró un tercero (p. ej. Costa Delivery a Andina) y no se le factura a MADECI:{' '}
            <b>entra al stock</b> con su costo neto y bruto y queda la deuda con el proveedor. No va
            al registro de compras del SII ni da crédito de IVA.
          </span>
        </section>
      )}

      {tipo === 'FCC' && (
        <section className="franja-compra" aria-label="Cómo llegó la mercadería">
          <span className="etiqueta-c21">¿Cómo llegó la mercadería?</span>
          <div className="segmentos" role="radiogroup" aria-label="Cómo llegó la mercadería">
            {(
              [
                ['AHORA', 'Llegó con esta factura · entra al stock'],
                [
                  'GUIAS',
                  `Ya entró con guías${pendientesGuias ? ` (${pendientesGuias} por facturar)` : ''}`,
                ],
                ['DESPUES', 'Llegará después / es un gasto'],
              ] as const
            ).map(([v, t]) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={llegada === v}
                className={llegada === v ? 'activo' : ''}
                onClick={() => {
                  setLlegada(v);
                  if (v === 'GUIAS' && proveedor) setDialogo({ tipo: 'guias' });
                  if (v !== 'GUIAS') setLineas((ls) => ls.filter((l) => !l.recepcionLineaId));
                }}
              >
                {t}
              </button>
            ))}
          </div>
          {llegada === 'GUIAS' && proveedor && (
            <button
              type="button"
              className="boton-mini"
              onClick={() => setDialogo({ tipo: 'guias' })}
            >
              Traer guías…
            </button>
          )}
        </section>
      )}

      {/* ------------------------------------------------ grilla */}
      <div className="trabajo-compra">
        <div className="grilla-nv">
          <table className="grilla-c21 grilla-compra" aria-label="Líneas del documento">
            <thead>
              <tr>
                <th className="c-n">#</th>
                <th className="c-cod">Código</th>
                <th>Producto</th>
                <th className="c-uni">Unid.</th>
                <th className="al-der c-cant">{tipo === 'GRC' ? 'Llegó' : 'Cantidad'}</th>
                {tipo === 'GRC' ? (
                  <>
                    <th
                      className="al-der c-cant"
                      title="Roto, vencido o no aceptado: no entra al stock"
                    >
                      Rechazado
                    </th>
                    <th>Motivo del rechazo</th>
                    <th className="al-der c-cant">Entra</th>
                  </>
                ) : (
                  <>
                    <th className="al-der c-precio">Precio neto</th>
                    <th
                      className="al-der c-desc"
                      title="Porcentaje (30,28) o el monto del descuento con $ delante ($6300)"
                    >
                      % Desc.
                    </th>
                    {esFactura && (
                      <th
                        className="al-der c-flete"
                        title="Flete que el proveedor cobró en la línea del producto"
                      >
                        Flete línea
                      </th>
                    )}
                    <th className="al-der c-total">Total neto</th>
                    {esFactura && (
                      <th
                        className="al-der c-costo"
                        title="Costo final por unidad de compra: (neto + flete que le toca) ÷ cantidad"
                      >
                        Costo unit.
                      </th>
                    )}
                  </>
                )}
              </tr>
            </thead>
            <tbody ref={grilla}>
              {lineas.map((l, i) => {
                const entra = Math.max(0, num(l.cantidad) - num(l.rechazada));
                const fleteDe = fletes[i] ?? 0;
                const costoUnit = num(l.cantidad) > 0 ? (netos[i]! + fleteDe) / num(l.cantidad) : 0;
                const adicUnit =
                  num(l.cantidad) > 0 ? (adicionalDeLinea[i] ?? 0) / num(l.cantidad) : 0;
                // Lo que costaría la logística de la línea con el costo logístico vigente del
                // proveedor: se sugiere (no se escribe) para compararlo con el recargo de la factura.
                const logistico = (costos.get(l.productoId) ?? 0) * num(l.cantidad) * l.factor;
                const celda = (
                  col: string,
                  valor: string,
                  campo: keyof LineaCompra,
                  ancho = '',
                  sugerencia?: { texto: string; ayuda: string },
                ) => (
                  <input
                    className={`celda-num ${ancho}`}
                    inputMode="decimal"
                    data-clave={l.clave}
                    data-col={col}
                    aria-label={`${col} línea ${i + 1}`}
                    placeholder={sugerencia?.texto}
                    title={sugerencia?.ayuda}
                    value={valor}
                    onChange={(e) =>
                      cambiar(l.clave, {
                        [campo]: e.target.value.replace(',', '.'),
                      } as Partial<LineaCompra>)
                    }
                    onFocus={(e) => e.target.select()}
                    onKeyDown={(e) => teclaCelda(e, i, col)}
                  />
                );
                return (
                  <tr
                    key={l.clave}
                    data-linea={i + 1}
                    className={l.recepcionLineaId ? 'de-guia' : ''}
                  >
                    <td className="mono apagado celda-n">
                      <span>{i + 1}</span>
                      {!corrigeId && (
                        <button
                          type="button"
                          className="quitar"
                          tabIndex={-1}
                          title="Quitar línea (Ctrl+Supr)"
                          aria-label={`Quitar línea ${i + 1}`}
                          onClick={() => quitar(l.clave)}
                        >
                          ✕
                        </button>
                      )}
                    </td>
                    <td className="mono">{l.sku}</td>
                    <td className="producto" title={l.nombre}>
                      {l.nombre}
                      {l.recepcionLineaId && <span className="insignia tono-neutro">guía</span>}
                    </td>
                    <td>
                      <SelectorUnidad
                        empresaId={empresaId}
                        linea={l}
                        // La factura puede venir en otra unidad que la guía (Rodrigo, 30-10: la guía
                        // entró en CJ y la factura trae precios por UN): la línea de la guía también
                        // cambia de unidad y conserva lo recibido (10 CJ ×6 pasan a 60 UN).
                        editable={!corrigeId && (!!l.recepcionLineaId || !l.ordenLineaNumero)}
                        conservarBase={!!l.recepcionLineaId}
                        alCambiar={(p) => {
                          const factor = Number(p.factor_a_base) || 1;
                          const precio = num(l.precio)
                            ? String(Math.round((num(l.precio) / l.factor) * factor * 100) / 100)
                            : l.precio;
                          const cantidad =
                            l.recepcionLineaId && num(l.cantidad)
                              ? limpio((num(l.cantidad) * l.factor) / factor)
                              : l.cantidad;
                          cambiar(l.clave, {
                            presentacionId: p.id,
                            unidad: p.codigo,
                            factor,
                            precio,
                            cantidad,
                          });
                        }}
                      />
                    </td>
                    <td className="al-der celda-cantidad">
                      <CeldaCantidadCompra
                        empresaId={empresaId}
                        linea={l}
                        numero={i + 1}
                        editable={!corrigeId}
                        unidadFija={!!l.recepcionLineaId || !!l.ordenLineaNumero}
                        alCambiar={(c) => cambiar(l.clave, c)}
                        alSiguiente={() => avanzar(i, 'cantidad')}
                        alTecla={(e) => teclaCelda(e, i, 'cantidad')}
                      />
                    </td>
                    {tipo === 'GRC' ? (
                      <>
                        <td className="al-der">{celda('rechazada', l.rechazada, 'rechazada')}</td>
                        <td>
                          {num(l.rechazada) > 0 && (
                            <input
                              className="celda-texto"
                              data-clave={l.clave}
                              data-col="motivo"
                              aria-label={`Motivo línea ${i + 1}`}
                              placeholder="Ej.: 2 cajas rotas"
                              value={l.motivo}
                              onChange={(e) => cambiar(l.clave, { motivo: e.target.value })}
                              onKeyDown={(e) => teclaCelda(e, i, 'motivo')}
                            />
                          )}
                        </td>
                        <td className="al-der mono fuerte">{decimal(String(entra))}</td>
                      </>
                    ) : (
                      <>
                        <td className="al-der">{celda('precio', l.precio, 'precio')}</td>
                        <td className="al-der">{celda('desc', l.desc, 'desc')}</td>
                        {esFactura && (
                          <td className="al-der">
                            {celda(
                              'flete',
                              l.flete,
                              'flete',
                              '',
                              logistico > 0
                                ? {
                                    texto: pesos(String(Math.round(logistico))),
                                    ayuda: `Costo logístico vigente con el proveedor: ${pesos(String(costos.get(l.productoId)))} por unidad × ${decimal(String(num(l.cantidad) * l.factor))} = ${pesos(String(Math.round(logistico)))}. Compáralo con el recargo de la factura.`,
                                  }
                                : undefined,
                            )}
                          </td>
                        )}
                        <td className="al-der mono fuerte">{pesos(String(netos[i]))}</td>
                        {esFactura && (
                          <td
                            className="al-der mono"
                            title={
                              num(l.cantidad) > 0
                                ? `Neto ${pesos(String(netos[i]))} + flete ${pesos(String(fleteDe))} = ${pesos(String(netos[i]! + fleteDe))} · por unidad base ${pesos(String(Math.round(((netos[i]! + fleteDe) / (num(l.cantidad) * l.factor)) * 100) / 100))}${exento ? '' : ` · con IVA ${pesos(String(Math.round(costoUnit * 1.19)))}`}${adicUnit ? ` · ILA ${pesos(String(Math.round(adicUnit)))} · bruto ${pesos(String(Math.round(costoUnit * (exento ? 1 : 1.19) + adicUnit)))}` : ''}`
                                : undefined
                            }
                          >
                            {costoUnit ? pesos(String(Math.round(costoUnit))) : ''}
                            {fleteDe > 0 && <span className="apagado chico"> +flete</span>}
                          </td>
                        )}
                      </>
                    )}
                  </tr>
                );
              })}
              {!(tipo === 'FCC' && llegada === 'GUIAS') && !corrigeId && (
                <tr className="fila-digitacion">
                  <td className="mono apagado">{lineas.length + 1}</td>
                  <td colSpan={3}>
                    <input
                      ref={filaCodigo}
                      className="celda-codigo"
                      aria-label="Agregar producto"
                      placeholder={
                        proveedor
                          ? 'código → Enter → cantidad → Enter · F3 buscar'
                          : 'Primero elige el proveedor (F2)'
                      }
                      disabled={!proveedor || !!orden}
                      value={codigo}
                      onChange={(e) => setCodigo(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void ingresarCodigo(codigo);
                        }
                      }}
                    />
                  </td>
                  <td colSpan={tipo === 'GRC' ? 4 : esFactura ? 6 : 4} />
                </tr>
              )}
              {Array.from({ length: relleno }, (_, k) => (
                <tr key={`r${k}`} className="relleno" aria-hidden>
                  <td colSpan={tipo === 'GRC' ? 8 : esFactura ? 10 : 8}>&nbsp;</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* panel derecho: flete, impuestos y avisos */}
        <aside className="panel-compra" aria-label="Flete e impuestos">
          {esFactura && (
            <>
              <h3>Flete</h3>
              <label className="campo">
                <span className="etiqueta">
                  Flete total {tipo === 'ISF' ? 'del documento' : 'de la factura'}
                </span>
                <input
                  inputMode="numeric"
                  aria-label="Flete total"
                  value={fleteGlobal}
                  placeholder="0"
                  onChange={(e) => setFleteGlobal(e.target.value.replace(/[^\d]/g, ''))}
                />
              </label>
              <label className="campo">
                <span className="etiqueta">Se reparte</span>
                <select
                  aria-label="Cómo se reparte el flete"
                  value={reparto}
                  onChange={(e) => setReparto(e.target.value as Reparto)}
                >
                  <option value="PROVEEDOR">
                    Como el proveedor (
                    {estrategiaProv.data?.estrategia
                      ? (NOMBRE_REPARTO[
                          estrategiaProv.data.estrategia.estrategia as keyof typeof NOMBRE_REPARTO
                        ] ?? 'su regla')
                      : 'por valor'}
                    )
                  </option>
                  <option value="FLETE_POR_VALOR">Por valor</option>
                  <option value="FLETE_POR_UNIDADES">Por unidades</option>
                  <option value="FLETE_POR_COSTO_LOGISTICO">
                    Por costo logístico del proveedor
                  </option>
                </select>
              </label>
              <p className="ayuda">
                Si el proveedor cobra el flete <b>en la línea</b> del producto, escríbelo en la
                columna «Flete línea»: es de ese producto. Si lo cobra en un total, va aquí.
              </p>
              {faltanCostos && (
                <p className="tono-aviso" role="alert">
                  Falta el costo logístico de{' '}
                  {lineas
                    .filter((l) => !costos.get(l.productoId))
                    .map((l) => l.sku)
                    .join(', ')}
                  : pídeselo al proveedor y cárgalo en Compras › Proveedores › Costos logísticos.
                  Sin eso, el flete queda pendiente de repartir.
                </p>
              )}
              <h3>Impuestos</h3>
              <label className="campo">
                <span className="etiqueta">IVA {exento ? '(exenta)' : '19 %'}</span>
                <input
                  inputMode="numeric"
                  aria-label="IVA"
                  value={ivaManual ?? String(ivaCalculado)}
                  disabled={exento}
                  onChange={(e) => setIvaManual(e.target.value.replace(/[^\d]/g, ''))}
                />
              </label>
              {ivaManual !== null && (
                <button type="button" className="enlace" onClick={() => setIvaManual(null)}>
                  volver al IVA calculado
                </button>
              )}
              {adicionales.map((a) => (
                <div key={a.codigo}>
                  <label className="campo">
                    <span className="etiqueta">
                      {a.codigo} {porcentaje(a.tasa)} · sobre {pesos(String(a.base))} ·{' '}
                      {a.lineas.length} línea{a.lineas.length === 1 ? '' : 's'}
                    </span>
                    <input
                      inputMode="numeric"
                      aria-label={`Impuesto ${a.codigo}`}
                      value={adicManual[a.codigo] ?? String(a.calculado)}
                      onChange={(e) =>
                        setAdicManual((m) => ({
                          ...m,
                          [a.codigo]: e.target.value.replace(/[^\d]/g, ''),
                        }))
                      }
                    />
                  </label>
                  {adicManual[a.codigo] !== undefined && (
                    <button
                      type="button"
                      className="enlace"
                      onClick={() =>
                        setAdicManual((m) => {
                          const resto = { ...m };
                          delete resto[a.codigo];
                          return resto;
                        })
                      }
                    >
                      volver al calculado ({pesos(String(a.calculado))})
                    </button>
                  )}
                </div>
              ))}
              {impuestosProd.isLoading && <p className="ayuda">Leyendo el ILA de los productos…</p>}
              {impuestosProd.isError && (
                <p className="tono-aviso" role="alert">
                  No se pudo leer el ILA de los productos: recarga antes de grabar o digítalo abajo.
                </p>
              )}
              {sinTasa.length > 0 && (
                <p className="tono-aviso" role="alert">
                  {sinTasa.length} producto{sinTasa.length === 1 ? '' : 's'} con un impuesto sin
                  tasa vigente a la fecha de emisión ({sinTasa[0]!.error}). Revísalo en
                  Configuración › Impuestos.
                </p>
              )}
              <div className="fila-campos">
                <label className="campo">
                  <span className="etiqueta">Otro impuesto</span>
                  <input
                    aria-label="Código del impuesto adicional"
                    placeholder="código"
                    value={adicionalCodigo}
                    onChange={(e) => setAdicionalCodigo(e.target.value.toUpperCase())}
                  />
                </label>
                <label className="campo">
                  <span className="etiqueta">Monto</span>
                  <input
                    inputMode="numeric"
                    aria-label="Monto del impuesto adicional"
                    value={adicional}
                    onChange={(e) => setAdicional(e.target.value.replace(/[^\d]/g, ''))}
                  />
                </label>
              </div>
              {otroAdic > 0 && porCodigo.has(adicionalCodigo) && (
                <p className="tono-aviso" role="alert">
                  {adicionalCodigo} ya se calcula arriba: ajusta ese monto en vez de repetirlo.
                </p>
              )}
              <p className="ayuda">
                Cada impuesto específico se calcula sobre los productos que lo llevan según su ficha
                (sin el flete) y queda imputado a cada línea. Si la factura trae otro monto,
                ajústalo al de la factura.
              </p>
            </>
          )}
          {tipo === 'GRC' && (
            <p className="ayuda">
              Al grabar, lo que llegó bueno <b>entra al stock</b> y queda disponible para vender. Lo
              rechazado no entra; cuando llegue la factura, lo que no entró se reclama al proveedor
              y el pago lo retiene hasta su nota de crédito.
            </p>
          )}
          {tipo === 'OCC' && (
            <p className="ayuda">
              La orden de compra no mueve stock: queda para aprobar y enviar al proveedor. Cuando
              llegue la mercadería, se recibe con una guía contra esta OC.
            </p>
          )}
          <label className="campo">
            <span className="etiqueta">Observación</span>
            <textarea
              aria-label="Observación"
              rows={2}
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
            />
          </label>
        </aside>
      </div>

      {(error || resultado || lineasMalas.length > 0 || mezclaGuias) && (
        <div className="avisos-compra" aria-live="polite">
          {error ? <PanelError error={error} /> : null}
          {resultado && <p className="tono-ok">{resultado}</p>}
          {lineasMalas.length > 0 && lineas.length > 0 && (
            <p className="apagado">
              {tipo === 'GRC'
                ? 'Cada línea necesita lo que llegó; si algo se rechazó, su motivo.'
                : 'Cada línea necesita cantidad y precio.'}
            </p>
          )}
          {mezclaGuias && (
            <p className="tono-aviso">
              Para cruzar con guías, todas las líneas deben venir de una guía (quita las otras).
            </p>
          )}
        </div>
      )}

      {/* ------------------------------------------------ pie */}
      <section className="pie-nv" aria-label="Totales">
        <dl className="izq">
          <div className="b">
            <dt>Neto productos</dt>
            <dd className="mono">{pesos(String(sumaLineas))}</dd>
          </div>
          {esFactura && (
            <div className="b">
              <dt>Flete</dt>
              <dd className="mono">{pesos(String(fletesLinea + global))}</dd>
            </div>
          )}
          {tipo !== 'GRC' && (
            <div className="b">
              <dt>Neto</dt>
              <dd className="mono">{pesos(String(neto))}</dd>
            </div>
          )}
          {esFactura && (
            <>
              <div className="b">
                <dt>IVA</dt>
                <dd className="mono">{pesos(String(iva))}</dd>
              </div>
              {adicionales
                .filter((a) => a.monto > 0)
                .map((a) => (
                  <div className="b" key={a.codigo}>
                    <dt>{a.codigo}</dt>
                    <dd className="mono">{pesos(String(a.monto))}</dd>
                  </div>
                ))}
              {otroAdic > 0 && (
                <div className="b">
                  <dt>{adicionalCodigo || 'Otro imp.'}</dt>
                  <dd className="mono">{pesos(String(otroAdic))}</dd>
                </div>
              )}
            </>
          )}
        </dl>
        <div className="der">
          <div className="lbl">
            <span>
              {tipo === 'GRC'
                ? 'UNIDADES'
                : tipo === 'OCC'
                  ? 'TOTAL NETO OC'
                  : tipo === 'ISF'
                    ? 'TOTAL A PAGAR'
                    : 'TOTAL FACTURA'}
            </span>
            <span>
              {lineas.length} línea{lineas.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="v mono" data-total>
            {tipo === 'GRC'
              ? decimal(
                  String(
                    lineas.reduce((s, l) => s + Math.max(0, num(l.cantidad) - num(l.rechazada)), 0),
                  ),
                )
              : pesos(String(total))}
          </div>
          <div className="cr">
            {puede(permiso[tipo]) ? (
              <button
                type="button"
                className="primario grabar-compra"
                disabled={!puedeGrabar}
                onClick={grabar}
                title="F9"
              >
                {accion.isPending ? 'Grabando…' : etiquetaGrabar} <kbd>F9</kbd>
              </button>
            ) : (
              <span>Sin permiso para grabar {NOMBRE_DOC_COMPRA[tipo].toLowerCase()}</span>
            )}
          </div>
        </div>
      </section>

      {dialogo?.tipo === 'proveedor' && (
        <BuscadorCliente
          empresaId={empresaId}
          rol="proveedor"
          alElegir={(c) => {
            setProveedor({
              id: c.id,
              rut: c.rut,
              dv: c.dv,
              razonSocial: c.razon_social,
              plazoDias: c.plazo_dias,
            });
            setDialogo(null);
            setTimeout(() => filaCodigo.current?.focus(), 30);
          }}
          alCerrar={() => setDialogo(null)}
        />
      )}
      {dialogo?.tipo === 'origen' && (
        <BuscadorCliente
          empresaId={empresaId}
          rol="proveedor"
          alElegir={(c) => {
            setOrigen({
              id: c.id,
              rut: c.rut,
              dv: c.dv,
              razonSocial: c.razon_social,
              plazoDias: c.plazo_dias,
            });
            setDialogo(null);
          }}
          alCerrar={() => setDialogo(null)}
        />
      )}
      {dialogo?.tipo === 'producto' && (
        <BuscadorProducto
          empresaId={empresaId}
          listaId={null}
          ubicacionId={bodegaId}
          ubicaciones={bodegas.lista}
          textoInicial={dialogo.texto}
          {...(puede('catalogo.productos.gestionar')
            ? { alCrear: (t: string) => setDialogo({ tipo: 'nuevoProducto', texto: t }) }
            : {})}
          alElegir={(e: ProductoElegido) => {
            setDialogo(null);
            void (async () => {
              const p =
                e.presentacion.id === e.base.id
                  ? await presentacionDeCompra(e.producto.id, e.base)
                  : e.presentacion;
              await agregarProducto(e.producto, p);
            })();
          }}
          alCerrar={() => setDialogo(null)}
        />
      )}
      {dialogo?.tipo === 'nuevoProducto' && (
        <NuevoProducto
          empresaId={empresaId}
          inicial={
            /^[A-Z0-9][A-Z0-9._-]{1,39}$/i.test(dialogo.texto) && /\d/.test(dialogo.texto)
              ? { sku: dialogo.texto }
              : { nombre: dialogo.texto }
          }
          alCreado={(creado) => {
            setDialogo(null);
            // El producto recién creado entra a la línea como si se hubiera elegido (en su caja).
            void (async () => {
              const exacto = await resolverCodigo(empresaId, creado.sku).catch(() => null);
              if (!exacto) return;
              const p = await presentacionDeCompra(exacto.producto.id, exacto.base);
              await agregarProducto(exacto.producto, p);
            })();
          }}
          alCerrar={() => setDialogo(null)}
        />
      )}
      {dialogo?.tipo === 'guias' && (
        <ElegirGuias
          guias={guias.data ?? []}
          cargando={guias.isLoading}
          yaTraidas={new Set(lineas.map((l) => l.recepcionLineaId).filter((x): x is string => !!x))}
          alElegir={(g) => {
            traerGuias(g);
            setDialogo(null);
          }}
          alCerrar={() => setDialogo(null)}
        />
      )}
    </div>
  );
}

function ElegirGuias({
  guias,
  cargando,
  yaTraidas,
  alElegir,
  alCerrar,
}: {
  guias: GuiaPorFacturar[];
  cargando: boolean;
  yaTraidas: Set<string>;
  alElegir: (g: GuiaPorFacturar[]) => void;
  alCerrar: () => void;
}) {
  const disponibles = guias.filter((g) => g.lineas.some((l) => !yaTraidas.has(l.recepcionLineaId)));
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  return (
    <Modal
      titulo="Guías recibidas sin factura"
      alCerrar={alCerrar}
      ancho="angosto"
      pie={
        <>
          <button type="button" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            type="button"
            className="primario"
            disabled={!marcadas.size}
            onClick={() => alElegir(disponibles.filter((g) => marcadas.has(g.recepcionId)))}
          >
            Traer {marcadas.size || ''} guía{marcadas.size === 1 ? '' : 's'}
          </button>
        </>
      }
    >
      {cargando && <p className="apagado">Cargando…</p>}
      {!cargando && !disponibles.length && (
        <p className="apagado">Este proveedor no tiene guías pendientes de facturar.</p>
      )}
      {disponibles.map((g) => (
        <label key={g.recepcionId} className="opcion-radio fila-guia">
          <input
            type="checkbox"
            aria-label={`Guía ${g.numeroVisible}`}
            checked={marcadas.has(g.recepcionId)}
            onChange={(e) =>
              setMarcadas((m) => {
                const n = new Set(m);
                if (e.target.checked) n.add(g.recepcionId);
                else n.delete(g.recepcionId);
                return n;
              })
            }
          />
          <span className="mono">{g.numeroVisible}</span>
          <span>{fecha(g.fecha)}</span>
          {g.guia && <span className="apagado">{g.guia}</span>}
          <span className="apagado">
            {g.lineas.length} producto{g.lineas.length === 1 ? '' : 's'}
          </span>
        </label>
      ))}
    </Modal>
  );
}
